import React, { useCallback, useMemo } from 'react';

import getImageURL from '@monolith/helpers/images';

import { createOnboardingTopBarPlugins } from '../features/onboarding-navigation/application/createOnboardingTopBarPlugins';
import { createLegacyNavigation } from '../features/onboarding-navigation/application/createLegacyNavigation';
import { useCurrentAccount } from '../features/onboarding-navigation/infrastructure/useCurrentAccount';
import { LegacyNavigationIcon } from '../features/onboarding-navigation/presentation/LegacyNavigationIcon';
import { PendoProvider } from '../features/topbar-plugins/pendo/PendoContext';
import { getAppUrl, getMainAppUrl } from '../helpers/loginUrl';
import {
    NavigationItem,
    NavigationShell,
    NavigationShellPlugin,
} from '../packages/navigation-shell';

export type OnboardingShellBusiness = {
    name?: string | null;
    fullName?: string | null;
    status?: string | null;
    timezone?: string | null;
    uiLogoUrl?: string | null;
    uiPrimaryColor?: string | null;
    smsFeatureEnabled?: boolean | null;
    federatedLoginEnabled?: boolean | null;
    marketingPlusEnabled?: boolean | null;
    campaignFeatureEnabled?: boolean | null;
    ongoingCampaignsEnabled?: boolean | null;
    crmEnabled?: boolean | null;
    advancedPayrollSettingsEnabled?: boolean | null;
    voipEnabled?: boolean | null;
    advancedReportingEnabled?: boolean | null;
    webhooksEnabled?: boolean | null;
    lessonMateFeatureEnabled?: boolean | null;
    videoRecordingRequestEnabled?: boolean | null;
    mailchimpFeatureEnabled?: boolean | null;
};

export interface OnboardingShellProps {
    business: OnboardingShellBusiness;
    email: string;
    children: React.ReactNode;
    onBackLinkClick?: (
        event: React.MouseEvent<HTMLAnchorElement>,
        url: string,
    ) => void;
}

export const OnboardingShell: React.FC<OnboardingShellProps> = ({
    business,
    email,
    children,
    onBackLinkClick,
}) => {
    const businessSlug = business.name || '';
    const displayName = business.fullName || business.name || 'Business';
    const mainAppUrl = businessSlug ? getMainAppUrl(businessSlug) : '#';
    const appUrl = getAppUrl();
    const pendoApiKey = process.env.PENDO_API_KEY || '';
    const logoUrl = business.uiLogoUrl
        ? getImageURL(business.uiLogoUrl, null, null, 'service')
        : `${mainAppUrl}/assets/opus1io_icon.png`;
    const {
        account,
        accounts,
        capabilities,
        loading: accountLoading,
    } = useCurrentAccount(email, businessSlug);
    const profilePhotoUrl = account?.profilePhoto
        ? getImageURL(account.profilePhoto, null, null, 'profile')
        : null;
    const requestNavigation = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>, url: string) => {
            if (!onBackLinkClick) {
                return;
            }
            event.preventDefault();
            onBackLinkClick(event, url);
        },
        [onBackLinkClick],
    );
    const handleNavigate = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>, item: NavigationItem) => {
            if (!item.target) {
                return;
            }

            const url =
                item.target.kind === 'local'
                    ? item.target.to
                    : item.target.href;
            requestNavigation(event, url);
        },
        [requestNavigation],
    );
    const sections = useMemo(
        () =>
            createLegacyNavigation({
                businessName: businessSlug,
                canGoToInvoiceReporting: capabilities.canGoToInvoiceReporting,
                canGoToTransactionReporting:
                    capabilities.canGoToTransactionReporting,
                canUpdateSuperuserTools: capabilities.canUpdateAllBusinesses,
                includeSuperuserTools: capabilities.canAccessAllBusinesses,
                mainAppUrl,
            }),
        [
            businessSlug,
            capabilities.canAccessAllBusinesses,
            capabilities.canGoToInvoiceReporting,
            capabilities.canGoToTransactionReporting,
            capabilities.canUpdateAllBusinesses,
            mainAppUrl,
        ],
    );
    const plugins = useMemo<NavigationShellPlugin[]>(
        () =>
            accountLoading
                ? []
                : createOnboardingTopBarPlugins({
                      accountCount: accounts.length,
                      appUrl,
                      businessName: businessSlug,
                      capabilities,
                      currentUserId: account?.id,
                      email,
                      mainAppUrl,
                      onNavigate: onBackLinkClick
                          ? requestNavigation
                          : undefined,
                      pendoEnabled: Boolean(pendoApiKey),
                      profilePhotoUrl,
                      smsFeatureEnabled: business.smsFeatureEnabled,
                      staffRole: account?.staffRole,
                      voipEnabled: business.voipEnabled,
                  }),
        [
            account?.id,
            account?.staffRole,
            accounts.length,
            accountLoading,
            appUrl,
            business.smsFeatureEnabled,
            business.voipEnabled,
            businessSlug,
            capabilities,
            email,
            mainAppUrl,
            onBackLinkClick,
            pendoApiKey,
            profilePhotoUrl,
            requestNavigation,
        ],
    );

    return (
        <PendoProvider
            account={account}
            apiKey={pendoApiKey}
            business={business}
        >
            <NavigationShell
                brand={{
                    name: displayName,
                    logoUrl,
                    homeTarget: {
                        kind: 'external',
                        href: mainAppUrl,
                    },
                }}
                sections={sections}
                accentColor={business.uiPrimaryColor}
                defaultSidebarCollapsed
                plugins={plugins}
                renderIcon={(icon) => <LegacyNavigationIcon icon={icon} />}
                onNavigate={handleNavigate}
            >
                {children}
            </NavigationShell>
        </PendoProvider>
    );
};

export default OnboardingShell;
